/**
 * Users Service
 *
 * API calls for user management.
 */

import { coreApiClient } from '@/api/core-client';
import type {
  UserListItem,
  UsersListResponse,
  CreateUserInput,
  CreateUserResponse,
  UpdateUserInput,
  UpdateUserRoleInput,
  GetUsersQueryParams,
  ResendActivationResponse,
  UserRole,
} from '@bstock/shared';

export interface UpdateModuleRolesInput {
  bstockRole?: UserRole | null;
  bstaffRole?: UserRole | null;
  bdisplayRole?: UserRole | null;
}

// Re-export types for convenience
export type {
  UserListItem,
  UsersListResponse,
  CreateUserInput,
  UpdateUserInput,
  UpdateUserRoleInput,
  GetUsersQueryParams,
};

interface ApiSuccess<T> {
  status: string;
  data: T;
  message?: string;
}

export interface PaginatedData {
  items: UserListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const usersService = {
  /**
   * List users with pagination and filters
   */
  async list(params: GetUsersQueryParams): Promise<PaginatedData> {
    const response = await coreApiClient.get<ApiSuccess<PaginatedData>>('/users', { params });
    return response.data.data;
  },

  /**
   * Create a new user
   */
  async create(data: CreateUserInput): Promise<CreateUserResponse> {
    const response = await coreApiClient.post<ApiSuccess<UserListItem> & { message?: string }>('/users', data);
    return {
      user: response.data.data,
      message: response.data.message || "Utilisateur cree. Email d'activation envoye.",
    };
  },

  /**
   * Update a user
   */
  async update(id: string, data: UpdateUserInput): Promise<UserListItem> {
    const response = await coreApiClient.patch<ApiSuccess<UserListItem>>(`/users/${id}`, data);
    return response.data.data;
  },

  /**
   * Update user role
   */
  async updateRole(id: string, data: UpdateUserRoleInput): Promise<UserListItem> {
    const response = await coreApiClient.patch<ApiSuccess<UserListItem>>(`/users/${id}/role`, data);
    return response.data.data;
  },

  /**
   * Delete a user
   */
  async delete(id: string): Promise<void> {
    await coreApiClient.delete(`/users/${id}`);
  },

  /**
   * Resend activation email
   */
  async resendActivation(id: string): Promise<ResendActivationResponse> {
    const response = await coreApiClient.post<ApiSuccess<never> & { message?: string }>(`/users/${id}/resend-activation`);
    return { message: response.data.message || "Email d'activation renvoye" };
  },

  /**
   * Update les roles per-module (US-MAC.8) sur un user pour la brand active.
   * Accepte `null` pour retirer l'acces a un module ("aucun acces").
   */
  async updateModuleRoles(
    userId: string,
    brandId: string,
    input: UpdateModuleRolesInput,
  ): Promise<void> {
    await coreApiClient.put(`/users/${userId}/brands/${brandId}/module-roles`, input);
  },
};
